import moment from "moment";
import { prepareNwpDataForChart } from "./nwp";
import { prepareGcosDataForChart } from "./gcos";

export const prepareDataForChart = (fileType, props) => {
  let ret;
  if (
    fileType === "synop" ||
    fileType === "temp" ||
    fileType === "ship" ||
    fileType === "buoy" ||
    fileType === "marine_surface" ||
    fileType === "wis2gtssynop" ||
    fileType === "wis2gtstemp"
  ) {
    ret = prepareNwpDataForChart(fileType, props);
  } else if (fileType === "gsn" || fileType === "guan") {
    ret = prepareGcosDataForChart(fileType, props);
  }
  return ret;
};

// `formatPeriodSeries` is used to format the observations
// aggregated by periods (opposed to the individual observations)
// The returned object is of the following structures
// For availability:
// {
// dwd: { data: [{ x: "2019-07-17T06:00:00Z": y: 2 }, { x: "2019-07-17T12:00:00Z", y: null }, { x: "2019-07-17T18:00:00Z", y: 2 }],
// max: 2, min: 0, representAs: 'lines' }
// ecmwf: { data: Array,  max: 6, min: 0, representAs: 'lines' }
// expected: { data: [{ x: "2019-07-17T06:00:00Z", y: 6 }, { x: "2019-07-17T12:00:00Z", y: null }, { x: "2019-07-17T18:00:00Z", y: 6 }],
// max: 6, min: 0, representAs: 'lines' }
// jma: { data: Array, max: 6, min: 0, representAs: 'lines' }
// ncep: { data: Array, max: 6, min: 0, representAs: 'lines' }
// }

// For Quality, six-hour, simple (here for ECMWF):
// {
// ecmwf:
// data: [{x: "2019-07-09T18:00:00Z", y: null}, {x: "2019-07-10T00:00:00Z", y: 0.211500002692143}, {x: "2019-07-10T06:00:00Z", y: 0.479666670163472},
// {x: "2019-07-10T12:00:00Z", y: 0.168499998748303}],
// max: 0.560166656970978,
// min: -0.158333333170352,
// representAs: 'points'
// }
export const formatPeriodSeries = (
  data,
  seriesNames,
  allTimeSteps,
  allTimeStepsNull,
  keyData,
  keyExpected,
  props
) => {
  // Check if keyData is an array (multi-series) or single value
  const isMultiSeries = Array.isArray(keyData);

  if (isMultiSeries) {
    // Handle multi-series case (e.g., gts-to-wis2 with nr_received_wis2 and nr_received_gts)
    let series = {};

    // Create series for each keyData field
    keyData.forEach((key, index) => {
      const seriesKey = key; // Use the field name as series key
      const label =
        key === "rms"
          ? "RMSE"
          : key === "gross_error_percent"
          ? "GE%"
          : key === "nr_received_wis2"
          ? "WIS2 Messages"
          : key === "nr_received_gts"
          ? "GTS Messages"
          : key;

      const color =
        key === "rms"
          ? "#1f77b4" // Blue for RMSE
          : key === "gross_error_percent"
          ? "#ff7f0e" // Orange for GE%
          : key === "nr_received_wis2"
          ? "#1f77b4"
          : key === "nr_received_gts"
          ? "#ff7f0e"
          : "#2ca02c";

      series[seriesKey] = {
        data: allTimeStepsNull.map(a => ({ ...a })),
        max: -Infinity,
        min: Infinity,
        label: label,
        color: color,
        representAs: "lines",
        tooltip: "point"
      };
    });

    // Populate data for each series
    data.forEach(function(d) {
      const indexToUpdate = allTimeSteps.indexOf(d.date);

      keyData.forEach(key => {
        if (d[key] !== undefined && d[key] !== null) {
          series[key].data[indexToUpdate] = {
            x: d.date,
            y: d[key]
          };

          if (d[key] > series[key].max) {
            series[key].max = d[key];
          }
          if (d[key] < series[key].min) {
            series[key].min = d[key];
          }
        }
      });
    });

    return series;
  }

  // Original single-series logic
  let series = {};
  seriesNames.forEach(function(serie) {
    series[serie.name] = {
      data: allTimeStepsNull.map(a => ({ ...a })),
      max: -Infinity,
      min: Infinity,
      label: serie.label,
      color: serie.color,
      representAs: serie.representAs,
      tooltip: serie.tooltip
    };
  });
  // Edit the values based on the keys `keyData`
  // and `keyExpected` (if it's not null)
  data.forEach(function(d) {
    const center = d.center.toLowerCase();
    const indexToUpdate = allTimeSteps.indexOf(d.date);
    series[center].data[indexToUpdate] = {
      x: d.date,
      y: d[keyData]
    };

    if (props.selectedReport === "timeliness" && props.combinedMode) {
      series[center].data[indexToUpdate].nr_negative_timeliness =
        d.nr_negative_timeliness || 0;
    }
    if (
      props.fileType === "wis2gtssynop" ||
      (props.fileType === "wis2gtstemp" && props.combinedMode)
    ) {
      series[center].data[indexToUpdate].nr_received = d.nr_received || 0;
    }
    if (d[keyData] > series[center].max) {
      series[center].max = d[keyData];
    }
    if (d[keyData] && d[keyData] < series[center].min) {
      series[center].min = d[keyData];
    }

    if (keyExpected) {
      series[keyExpected].data[indexToUpdate] = {
        x: d.date,
        y: d[keyExpected]
      };
      if (d[keyExpected] > series[keyExpected].max) {
        series[keyExpected].max = d[keyExpected];
      }
      if (d[keyExpected] && d[keyExpected] < series[keyExpected].min) {
        series[keyExpected].min = d[keyExpected];
      }
    }
  });

  return series;
};

export const formatPeriodSeriesBufrVsTac = (
  data,
  seriesNames,
  allTimeSteps,
  allTimeStepsNull,
  keyData,
  keyExpected,
  props
) => {
  // initialize an object with one key per center
  // plus one for the expected values
  // For each one set its value as a copy of `allTimeStepsNull`
  let hasBufrTacData = false;
  let series = {};
  series["bufr_nr_received"] = {
    data: [],
    max: -Infinity,
    min: Infinity,
    label: "Bufr Nr Received",
    color: "blue",
    representAs: "lines",
    tooltip: "point"
  };
  series["tac_nr_received"] = {
    data: [],
    max: -Infinity,
    min: Infinity,
    label: "Tac Nr Received",
    color: "red",
    representAs: "lines",
    tooltip: "point"
  };
  data.forEach(function(d) {
    if (d.bufr_nr_received || d.tac_nr_received) {
      hasBufrTacData = true;
      // Append to the data array instead of overwriting
      series["bufr_nr_received"].data.push({
        x: d.date,
        y: d.bufr_nr_received
      });
      // Update min and max values
      if (d.bufr_nr_received > series["bufr_nr_received"].max) {
        series["bufr_nr_received"].max = d.bufr_nr_received;
      }
      if (
        d.bufr_nr_received &&
        d.bufr_nr_received < series["bufr_nr_received"].min
      ) {
        series["bufr_nr_received"].min = d.bufr_nr_received;
      }

      series["tac_nr_received"].data.push({
        x: d.date,
        y: d.tac_nr_received
      });
      if (d.tac_nr_received > series["tac_nr_received"].max) {
        series["tac_nr_received"].max = d.tac_nr_received;
      }
      if (
        d.tac_nr_received &&
        d.tac_nr_received < series["tac_nr_received"].min
      ) {
        series["tac_nr_received"].min = d.tac_nr_received;
      }
    }
  });

  return series;
};

// Utility functions

// rangeBetweenDates returns an array of all the periods (six-hour or daily)
// that are between `dateStart` and a number of days (`nbrDays`)

// exported only for test
export const rangeBetweenDates = (
  dateStart,
  nbrDays,
  selectedSixHPeriod,
  sixHourPeriods = []
) => {
  const s = moment.utc(dateStart);
  let arr = [];
  // Add entries for dateStart
  if (sixHourPeriods.length > 0) {
    const currentIndex = sixHourPeriods.indexOf(selectedSixHPeriod);
    const subSixHourPeriods = sixHourPeriods.slice(currentIndex);

    for (let j = 0; j < subSixHourPeriods.length; j++) {
      arr.push(
        s.utc().format("YYYY-MM-DD") + "T" + subSixHourPeriods[j] + ":00:00Z"
      );
    }
  } else {
    arr.push(s.utc().format("YYYY-MM-DD") + "T00:00:00Z");
  }
  // Add entries for older days
  for (let i = 1; i <= nbrDays - 1; i++) {
    // always substract 1 because substract mutates the original moment by subtracting time.
    const t = s.subtract(1, "days");
    if (sixHourPeriods.length > 0) {
      for (let j = 0; j < sixHourPeriods.length; j++) {
        arr.push(
          t.utc().format("YYYY-MM-DD") + "T" + sixHourPeriods[j] + ":00:00Z"
        );
      }
    } else {
      arr.push(t.utc().format("YYYY-MM-DD") + "T00:00:00Z");
    }
  }
  // For the last day - in case of six-hour periods - we take the periods
  // up to selectedSixHPeriod
  const t = s.subtract(1, "days");
  if (sixHourPeriods.length > 0) {
    const currentIndex = sixHourPeriods.indexOf(selectedSixHPeriod);
    const subSixHourPeriods = sixHourPeriods.slice(0, currentIndex + 1);
    for (let j = 0; j < subSixHourPeriods.length; j++) {
      arr.push(
        t.utc().format("YYYY-MM-DD") + "T" + subSixHourPeriods[j] + ":00:00Z"
      );
    }
  } else {
    arr.push(t.utc().format("YYYY-MM-DD") + "T00:00:00Z");
  }
  return arr.reverse();
};

// Similar to 'rangeBetweenDates' above but with months
export const rangeMonthsBetweenDates = (dateStart, nbrMonths) => {
  const s = moment.utc(dateStart);
  let arr = [];

  arr.push(s.utc().format("YYYY-MM-DD") + "T00:00:00Z");

  for (let i = 1; i <= nbrMonths; i++) {
    const t = s.subtract(1, "months");
    arr.push(t.utc().format("YYYY-MM-DD") + "T00:00:00Z");
  }

  return arr.reverse();
};

export const updateTimeSteps = (series, allTimeSteps) => {
  // Update allTimeSteps to include the individual dates from all series
  // This is used when we are dealing with individual observations
  // In this case the time-steps can be different from one serie to another

  // allTimeSteps includes all the time-steps during 30 days where a step
  // is every 6 or 24h

  // get all the unique individuals time-steps
  let individualSteps = [];
  for (const serie in series) {
    individualSteps.push(series[serie].data.map(d => d.x));
  }
  individualSteps = Array.from(new Set(individualSteps.flat())).sort();

  // get the period dates that are before and after the range of individuals dates
  // in case the range of individual observations doesn't cover 30 days
  const olderDates = allTimeSteps.filter(d => d < individualSteps[0]);
  const newerDates = allTimeSteps.filter(
    d => d > individualSteps[individualSteps.length - 1]
  );
  // merge the 3 arrays
  return [...olderDates, ...individualSteps, ...newerDates];
};

// `getColorByKey` is used for SYNOP/Quality/Six-hour/Simple
// This the only case where the color
// of a serie in the chart is not static.
// If the property `key` of the object `obj` is equals to `ok`,
// returns the color green, red otherwise.
// This function is executed when creating the chart (in utils/charts.js)
export const getColorByKey = (obj, key, ok, report) => {
  if (report === "timeliness") {
    return "#1a9850";
  }
  return obj[key] === ok ? "#1a9850" : "#d73027";
};
